// imports
import { useSelector } from 'react-redux';
import { Box, styled } from '@mui/material';

// local imports
import { RootState } from '../../store';
import useAwdLegend from '../../hooks/useAwdLegend';

/**
 * AwdLegend
 *
 * Renders legend content for the Available Water Data (AWD) layer.
 * Displays color-coded squares with their corresponding labels and units.
 * Designed to be rendered inside the LegendButton popup.
 *
 * @component
 * @returns {JSX.Element | null} The rendered legend content, or null if empty
 */

const textWrap = {
  overflowWrap: 'break-word' as const,
  wordBreak: 'normal' as const,
};

const LegendContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '0 8px',
}));

const LegendBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
}));

const LegendHeader = styled('h2')(() => ({
  fontSize: '1.2rem',
  fontWeight: 700,
  lineHeight: '20px',
  margin: 0,
  ...textWrap,
}));

const DateText = styled(Box)(() => ({
  fontSize: '1rem',
  fontWeight: 400,
  color: '#000',
  ...textWrap,
}));

const Divider = styled('hr')(() => ({
  width: 'calc(100% + 16px)',
  marginLeft: '-8px',
  border: 'none',
  borderTop: '1px solid #e0e0e0',
  marginTop: '6px',
  marginBottom: '6px',
}));

const ColorBoxesContainer = styled('ul')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  listStyle: 'none',
  margin: 0,
  padding: 0,
}));

const ColorBoxItem = styled('li')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

const ColorBox = styled(Box)(() => ({
  width: '24px',
  height: '24px',
  border: '1px solid #808080',
  borderRadius: '2px',
  flexShrink: 0,
}));

/**
 * No Data swatch: hollow box with a red backslash diagonal.
 */
const NoDataBox = styled(Box)(() => ({
  width: '24px',
  height: '24px',
  border: '1px solid #808080',
  borderRadius: '2px',
  flexShrink: 0,
  position: 'relative',
  background: '#fff',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '140%',
    height: '2px',
    backgroundColor: 'red',
    transformOrigin: 'top left',
    transform: 'rotate(45deg)',
  },
}));

const Label = styled(Box)(() => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: '#000',
  ...textWrap,
}));

const UnitsContainer = styled(Box)(() => ({
  display: 'flex',
  gap: '4px',
  marginTop: '8px',
  ...textWrap,
}));

const BoldLabel = styled(Box)(() => ({
  fontSize: '1rem',
  fontWeight: 700,
  color: '#000',
  flexShrink: 0,
}));

const OpacityNote = styled(Box)(() => ({
  fontSize: '0.85rem',
  fontStyle: 'italic',
  color: '#71767a',
  marginTop: '4px',
}));

const ScreenReaderOnly = styled('span')(() => ({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}));

const AwdLegend = () => {
  useAwdLegend();

  const legendContent = useSelector(
    (state: RootState) => state.ui.legendContent
  );

  const opacity = useSelector((state: RootState) => state.waterData.opacity);

  if (legendContent.length === 0) return null;

  return (
    <LegendContainer data-testid="awd-legend">
      {legendContent.map((content) => (
        <LegendBox key={content.id}>
          <LegendHeader>
            {content.name}
            {content.variableId && (
              <span style={{ fontWeight: 400, fontSize: '1rem' }}>
                {' ('}
                <a
                  href={content.variableLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Variable information"
                  style={{ color: '#1a73e8', textDecoration: 'underline' }}
                >
                  {content.variableId}
                </a>
                {')'}
              </span>
            )}
          </LegendHeader>
          {content.month && content.year && (
            <DateText>
              {content.month} {content.year}
            </DateText>
          )}

          <Divider />

          {content.colors && content.binLabels && (
            <ColorBoxesContainer aria-label="Color classification">
              {content.colors.map((color, index) => (
                <ColorBoxItem key={index}>
                  <ColorBox
                    aria-hidden="true"
                    sx={{ backgroundColor: color }}
                  />
                  <Label aria-hidden="true">{content.binLabels![index]}</Label>
                  <ScreenReaderOnly>
                    {content.binLabels![index]
                      .replace(/</g, 'less than ')
                      .replace(/>/g, 'greater than ')
                      .replace(/≤/g, 'less than or equal to ')
                      .replace(/≥/g, 'greater than or equal to ')}
                  </ScreenReaderOnly>
                </ColorBoxItem>
              ))}
              <ColorBoxItem>
                <NoDataBox aria-hidden="true" />
                <Label>No Data</Label>
              </ColorBoxItem>
            </ColorBoxesContainer>
          )}

          {opacity < 100 && content.colors && (
            <OpacityNote>Colors based on 100% data layer opacity</OpacityNote>
          )}

          {content.units && (
            <UnitsContainer>
              <BoldLabel>Units:</BoldLabel>
              <Label sx={{ color: '#565C65' }}>{content.units}</Label>
            </UnitsContainer>
          )}
        </LegendBox>
      ))}
    </LegendContainer>
  );
};

export default AwdLegend;
